import { forwardRef, useRef, useCallback, useMemo } from 'react';

import classNames from 'classnames';

import { useElementLayout } from '../../hooks/useElementLayout';
import soundEffects from '../../services/sounds/soundEffects';

import styles from './Button.module.scss';
import './Button.scss';
import type { IButtonProps } from './buttonTypes';

const getShadowHeight = (height: number) => {
  if (typeof height !== 'number' || height === 0) {
    return '3px';
  }
  const shadowHeight = height * 0.12;
  return shadowHeight > 4 ? '4px' : `${shadowHeight}px`;
};

const Button = forwardRef<HTMLButtonElement, IButtonProps>(
  (
    {
      className,
      classNameWrapper = '',
      classNameContent,
      btnType = 'main',
      iconLeft,
      iconRight,
      handleOnClick,
      children,
      dataTestId,
      classNameLeftIcon,
      withCrown,
      popupAnimation = 'default',
      ...restProps
    },
    forwardedRef,
  ) => {
    const internalRef = useRef<HTMLButtonElement | null>(null);

    const { height } = useElementLayout(internalRef);

    const setRefs = useCallback(
      (node: HTMLButtonElement | null) => {
        internalRef.current = node;

        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
        } else if (forwardedRef && 'current' in forwardedRef) {
          forwardedRef.current = node;
        }
      },
      [forwardedRef],
    );

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (typeof handleOnClick === 'function') {
        handleOnClick(e);
      }
      soundEffects.playSound('click');
    };

    const buttonClasses = classNames(
      'button',
      styles.button,
      `${styles[`button--${restProps.disabled ? 'gray' : btnType}`]}`,
      className,
    );

    const wrapperClasses = classNames({
      'animation-popup': popupAnimation === 'default',
      'button-wrapper': true,
      [classNameWrapper]: true,
      [styles.withCrown]: withCrown,
    });

    const bottomShadowHeight = getShadowHeight(height);

    const buttonStyle = useMemo(
      () => ({ ...restProps.style, '--bottom-shadow-height': bottomShadowHeight }),
      [bottomShadowHeight, restProps.style],
    );

    return (
      <div className={wrapperClasses} data-testid={dataTestId}>
        <button className={buttonClasses} onClick={handleClick} {...restProps} ref={setRefs} style={buttonStyle}>
          {iconLeft ? (
            <div className={classNames(styles.btnIconSide, styles.btnIconSideLeft, classNameLeftIcon)}>{iconLeft}</div>
          ) : null}
          <div className={classNames('button__content', classNameContent)}>{children}</div>
          {iconRight ? (
            <div className={classNames(styles.btnIconSide, styles.btnIconSideRight)}>{iconRight}</div>
          ) : null}
        </button>
      </div>
    );
  },
);

Button.displayName = 'Button';

export default Button;
